/*
Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/
import i18n from "i18next";
import { type MatrixClient } from "matrix-js-sdk";
import { backendFetch } from "../utils/backend";
import { guestOrigin, joinBrowserChallenge } from "./joinSession";

export async function recordingTarget(
  client: MatrixClient | undefined,
  search: string,
  signal: AbortSignal,
): Promise<URL> {
  const target = new URL("/record" + search, guestOrigin());
  const recordingId = target.searchParams
    .get("v")
    ?.replace(/\.(mp4|webm|mp3|ogg)$/, "");
  if (!recordingId || !/^[A-Za-z0-9_-]{1,128}$/.test(recordingId))
    throw new Error(i18n.t("instance.errors.not_found"));
  target.searchParams.set("v", recordingId);
  target.searchParams.delete("staffUser");
  target.searchParams.delete("theme");
  target.searchParams.delete("lang");
  // A shared recording never requires cabinet login or a staff session.
  if (target.searchParams.has("share")) return target;
  if (!client) throw new Error(i18n.t("instance.errors.sign_in_required"));
  const receiver = await joinBrowserChallenge(target.origin, signal);
  if (receiver.userId && receiver.userId !== client.getUserId())
    throw new Error(i18n.t("instance.ui.other_organizer"));
  if (receiver.userId !== client.getUserId()) {
    const response = await backendFetch(client, "/api/auth/join-session/link", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ recordingId, challenge: receiver.challenge }),
      signal,
    });
    const { code } = await response.json();
    target.searchParams.set("staffUser", client.getUserId()!);
    target.hash = "?" + new URLSearchParams({ handoff: code });
  }
  return target;
}
